import axios from 'axios';
import { DEMO_TOKEN, demoAdapter } from '../demo/adapter.js';

/**
 * Жалгыз axios инстанциясы. Компоненттер аны ТҮЗ чакырбайт — `endpoints.js`
 * аркылуу гана (SERVER_WEB_STACK §8.1).
 */
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
});

export const TOKEN_KEYS = Object.freeze({ ACCESS: 'accessToken', REFRESH: 'refreshToken' });

export function getAccessToken() {
  if (typeof window === 'undefined') return null;
  try { return localStorage.getItem(TOKEN_KEYS.ACCESS); } catch { return null; }
}

export function saveTokens({ accessToken, refreshToken }) {
  localStorage.setItem(TOKEN_KEYS.ACCESS, accessToken);
  if (refreshToken) localStorage.setItem(TOKEN_KEYS.REFRESH, refreshToken);
}

/** Сессияны бүтүрөт (тема аккаунтта сакталат — бул жерде тийбейбиз) */
export function clearSession() {
  localStorage.removeItem(TOKEN_KEYS.ACCESS);
  localStorage.removeItem(TOKEN_KEYS.REFRESH);
}

/**
 * `/files/...` дареги токен менен. `<img>`, pdf.js сыяктуу браузер өзү
 * жиберген сурамдарга Authorization башын коюуга болбойт — ошондуктан
 * сервер токенди `?token=` аркылуу да кабыл алат (өмүрү 15 мүнөт).
 */
export function fileUrl(path) {
  const token = getAccessToken();
  return token ? `${path}?token=${encodeURIComponent(token)}` : path;
}

api.interceptors.request.use((config) => {
  if (typeof window === 'undefined') return config;
  const token = getAccessToken();
  // Демо-режим: серверге барбай, жоопту `src/demo/adapter.js` берет
  if (token === DEMO_TOKEN) {
    config.adapter = () => demoAdapter(config);
    return config;
  }
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// 401'де бир жолу refresh — бир эле убакта келген 401'дер бир гана refresh сурамын күтөт
let refreshing = null;

function goToLogin() {
  clearSession();
  if (window.location.pathname !== '/login') {
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/login?next=${next}`;
  }
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const isAuthCall = original?.url?.startsWith('/auth/login') || original?.url?.startsWith('/auth/refresh');

    if (error.response?.status !== 401 || original?._retry || isAuthCall) {
      return Promise.reject(error);
    }
    original._retry = true;

    const storedRefresh = localStorage.getItem(TOKEN_KEYS.REFRESH);
    if (!storedRefresh) {
      goToLogin();
      return Promise.reject(error);
    }

    try {
      // Сервер жооп бербей туруп калса, бүт колдонмо катып калбашы үчүн — таймаут
      refreshing ||= api.post('/auth/refresh', { refreshToken: storedRefresh }, { timeout: 10_000 });
      const { data } = await refreshing;
      refreshing = null;
      saveTokens(data.data);
      original.headers.Authorization = `Bearer ${data.data.accessToken}`;
      return api(original);
    } catch (refreshError) {
      refreshing = null;
      goToLogin();
      return Promise.reject(refreshError);
    }
  },
);

/** Сервердин катасынан колдонуучуга көрсөтө турган текст */
export function errorMessage(error, fallback = 'Произошла ошибка') {
  return error?.response?.data?.message || error?.message || fallback;
}
